import { strings } from "./strings.js";

export type CaptureFailureKind = "needs-gesture" | "cancelled" | "failed";

export interface CaptureFailure {
  kind: CaptureFailureKind;
  message: string;
}

export function classifyCaptureFailure(error: unknown): CaptureFailure {
  if (error instanceof DOMException) {
    if (error.name === "InvalidStateError") {
      return {
        kind: "needs-gesture",
        message: strings.errors.capture.retryGesture,
      };
    }
    if (error.name === "AbortError" || error.name === "NotAllowedError") {
      return { kind: "cancelled", message: strings.errors.capture.cancelled };
    }
    return {
      kind: "failed",
      message: strings.errors.capture.failedWithName(error.name),
    };
  }
  return { kind: "failed", message: strings.errors.capture.failed };
}

export async function captureDisplay(): Promise<MediaStream> {
  const constraints = {
    video: {
      // Chromium extension; explicit per SPEC section 7.5.
      surfaceSwitching: "include",
    },
    audio: true,
  } as DisplayMediaStreamOptions;
  return navigator.mediaDevices.getDisplayMedia(constraints);
}

const syntheticCleanup = new WeakMap<MediaStream, () => void>();

/** Deterministic CI-only source; callers must gate this behind the E2E build flag. */
export function createSyntheticCanvasCapture(
  label = "KlassShare E2E · 안녕하세요",
): MediaStream {
  const canvas = document.createElement("canvas");
  canvas.width = 1280;
  canvas.height = 720;
  const context = canvas.getContext("2d");
  if (context === null) throw new Error("Canvas 2D is unavailable");
  context.fillStyle = "#143f52";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#ffffff";
  context.font = "600 54px system-ui, sans-serif";
  context.fillText(label, 72, 330);
  context.fillStyle = "#ef8354";
  context.fillRect(72, 382, 720, 12);
  const stream = canvas.captureStream(15);
  for (const track of stream.getVideoTracks()) track.contentHint = "detail";
  let frame = 0;
  const timer = setInterval(() => {
    frame += 1;
    context.fillStyle = frame % 2 === 0 ? "#ef8354" : "#60c0a8";
    context.fillRect(72, 430, 180, 70);
    context.fillStyle = "#ffffff";
    context.font = "500 30px ui-monospace, monospace";
    context.fillText(`frame ${String(frame).padStart(6, "0")}`, 84, 475);
  }, 80);
  const cleanup = () => clearInterval(timer);
  syntheticCleanup.set(stream, cleanup);
  for (const track of stream.getTracks())
    track.addEventListener("ended", cleanup, { once: true });
  return stream;
}

export function disposeCapture(stream: MediaStream): void {
  syntheticCleanup.get(stream)?.();
  syntheticCleanup.delete(stream);
  for (const track of stream.getTracks()) track.stop();
}
